// /modules — the canonical, pictorial features directory (every OFFERING_TABS group × feature row
// as a linkable module card). Copy is 100% derived via landingparts/moduleCatalog (parts2 one-liners
// + featureContent details); the headline count stays the claim-registry-backed 115 documented
// capabilities across nine workspaces. Buyer and capability guides explain that same catalogue
// and narrow shipped scope without inflating it.
// Chrome + motion follow the SwitchPage pattern:
// MarketingMotion + MarketingTopBar + SiteFooter, house EASE, and reduced-motion gating.
import React, { useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { m, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Layers3, LayoutGrid, ListChecks, Search, Sparkles, Workflow, X } from "lucide-react";
import { BookDemoLink, MarketingTopBar, SiteFooter, requestBookDemo } from "../components/marketing/shared";
import HeroCampusArt from "../components/marketing/HeroCampusArt";
import { MarketingMotion, useMarketingMotion } from "../components/marketing/MarketingMotion";
import { ScrollProgressBar } from "../components/marketing/scrollMotion";
import { Button } from "../components/ui/primitives";
import {
  AIRA_CAPABILITIES,
  MODULE_CATALOG,
  MODULE_WORKSPACES,
  resolveWorkspaceFilter,
} from "./landingparts/moduleCatalog";
import { init as initAnalytics, track } from "../lib/analytics";
import Module3DIcon from "../components/marketing/Module3DIcon";
import { feature3dColor, module3dAsset } from "../components/marketing/module3dAssets";
import MarketingSeo, { seoOrigin } from "../components/marketing/MarketingSeo";
import { moduleProfileFor } from "./landingparts/moduleProfiles";
import { MODULE_BUYER_GUIDES } from "./landingparts/moduleBuyerGuides";
import { CTA_PRIMARY_LABEL, CTA_SECONDARY_LABEL } from "../components/marketing/navigation";

// The ten-scene narrative is intentionally route-local and follows the concise catalogue brief.
// Keeping it deferred prevents ten product visuals from competing with the page hero's first paint.
const ProductStoryShowcase = React.lazy(() => import("./landingparts/ProductStoryShowcase"));

const EASE = [0.22, 1, 0.36, 1];
const GRID_STAGGER = { hidden: {}, show: { transition: { staggerChildren: 0.03, delayChildren: 0.05 } } };
const GRID_ITEM = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: EASE } } };

const buyerGuideTitle = (guide) => guide.navTitle || guide.shortTitle || guide.title || guide.key;
const buyerGuideSummary = (guide) => (
  guide.navSummary
  || guide.summary
  || guide.hero?.summary
  || guide.outcome
  || guide.description
  || "See the connected capabilities, users, evidence and rollout conditions for this school workflow."
);
const buyerGuideKind = (guide) => (
  guide.navKind
  || guide.typeLabel
  || guide.eyebrow
  || (guide.type === "capability-guide" ? "Capability guide" : "School workflow guide")
);

// Search aliases belong to the guide registry. Keep this helper exported and pure so a
// terminology change can be contract-tested without mounting the heavy product-story surface.
export function matchingBuyerGuides(guides, query) {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return [];
  return guides.filter((guide) => {
    const searchable = [
      guide.key,
      buyerGuideTitle(guide),
      guide.title,
      guide.shortTitle,
      buyerGuideSummary(guide),
      guide.type,
      guide.status,
      ...(guide.aliases || []),
      ...(guide.searchTerms || []),
      ...(guide.primaryCapabilityKeys || []),
      ...(guide.relatedCapabilityKeys || []),
    ].filter(Boolean).join(" ").toLowerCase();
    return searchable.includes(needle);
  });
}

// One module card — the WHOLE card is the link to /modules/:key. The feature-specific 3D render is
// the marketing identifier; its Lucide glyph remains the semantic badge and resilient fallback.
function ModuleCard({ mod, reduce }) {
  const accent = mod.aiFeature ? mod.groupColor : feature3dColor(mod.name, mod.groupColor);
  const profile = moduleProfileFor(mod);
  const audience = profile?.roles?.length ? profile.roles.slice(0, 2).join(" & ") : "Authorised school teams";
  const isModule = mod.kind === "ERP module";
  return (
    <m.div variants={reduce ? undefined : GRID_ITEM} className="h-full">
      <Link
        to={`/modules/${mod.key}`}
        className="group relative flex h-full min-h-[224px] flex-col overflow-hidden rounded-2xl border border-line bg-surface p-4 transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-1 hover:border-primary/25 hover:shadow-[0_18px_42px_-24px_rgba(15,23,42,0.38)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 sm:p-5"
        style={{ "--mc": accent }}
        data-testid={`module-card-${mod.key}`}
        onClick={() => track("module_card_opened", {
          module: mod.key,
          source_group: mod.group,
          workspace: mod.workspace,
        })}
      >
        <span className="absolute inset-x-0 top-0 h-[3px]" style={{ background: accent }} aria-hidden="true" />
        <div className="flex items-start justify-between gap-2">
          <Module3DIcon
            Icon={mod.Icon}
            src={module3dAsset(mod)}
            color={accent}
            size="md"
            className="transition-transform duration-200 group-hover:-translate-y-1 group-hover:rotate-2"
          />
          {mod.publicStatus && (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[12px] font-bold uppercase tracking-wide ${mod.aiFeature ? "bg-ai/10 text-ai-ink" : "bg-muted text-ink-2"}`}>
              {mod.aiFeature ? <Sparkles className="h-3 w-3" aria-hidden="true" /> : null}{mod.publicStatus}
            </span>
          )}
        </div>
        <span className="mt-3 block text-[12px] font-bold uppercase tracking-[0.1em] text-ink-subtle">{mod.kind}</span>
        <span className="mt-1 block text-[15px] font-semibold leading-tight text-ink [text-wrap:balance]">{mod.name}</span>
        <span className="mt-1.5 block text-[12.5px] leading-relaxed text-ink-subtle line-clamp-3">{mod.oneLiner}</span>
        <span className="mt-3 block text-[12px] font-semibold text-ink-subtle">For {audience}</span>
        <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-[12.5px] font-semibold text-primary">
          {isModule ? `Explore ${mod.name}` : "View scope and availability"} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </span>
      </Link>
    </m.div>
  );
}

function ModulesHubContent() {
  const prefersReducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const reduce = Boolean(prefersReducedMotion || paused);
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const requestedGroup = searchParams.get("group") || "all";
  const selectedGroup = resolveWorkspaceFilter(requestedGroup);

  useEffect(() => {
    initAnalytics();
    // /modules#<group> deep links (each ModulePage's "In the bigger picture" strip) land on the
    // group's own section; a plain /modules keeps the scroll-to-top behaviour.
    const target = (hash || "").replace(/^#/, "");
    const workspaceTarget = resolveWorkspaceFilter(target);
    if (
      target === "product-story"
      || target === "school-job-guides"
      || target === "module-search"
      || target === "module-catalogue"
      || target === "modules-close"
      || target === "aira"
      || (target && workspaceTarget !== "all" && workspaceTarget !== "aira")
    ) {
      const resolvedTarget = target === "aira"
        ? "aira"
        : workspaceTarget !== "all" && workspaceTarget !== "aira"
          ? workspaceTarget
          : target;
      requestAnimationFrame(() => document.getElementById(resolvedTarget)?.scrollIntoView({ block: "start" }));
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);
  useEffect(() => { track("modules_page_viewed", { modules: MODULE_CATALOG.length }); }, []);

  const visibleGroups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return MODULE_WORKSPACES
      .filter((workspace) => selectedGroup === "all" || selectedGroup === "aira" || workspace.k === selectedGroup)
      .map((group) => ({
        ...group,
        modules: group.modules.filter((mod) => {
          if (selectedGroup === "aira" && mod.crossWorkspaceLayer !== "aira") return false;
          return !needle || `${mod.name} ${mod.oneLiner} ${mod.kind} ${mod.workspaceTitle} ${(mod.roles || []).join(" ")}`.toLowerCase().includes(needle);
        }),
      }))
      .filter((group) => group.modules.length);
  }, [query, selectedGroup]);
  const visibleBuyerGuides = useMemo(
    () => matchingBuyerGuides(MODULE_BUYER_GUIDES, query),
    [query]
  );
  const resultCount = visibleGroups.reduce((total, group) => total + group.modules.length, 0);

  const updateFilter = (next) => {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(next)) value && value !== "all" ? params.set(key, value) : params.delete(key);
    setSearchParams(params, { replace: true });
  };

  const reveal = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 20 },
    whileInView: reduce ? undefined : { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.5, ease: EASE, delay: reduce ? 0 : delay },
  });

  return (
      <div className="min-h-screen bg-canvas text-ink flex flex-col landing-page" data-testid="modules-hub">
        <MarketingSeo
          title="School ERP Modules & Features | Vidyapeeth360"
          description="Explore 115 documented Vidyapeeth360 capabilities across nine school workspaces, with role, availability and connected-workflow context."
          path="/modules"
          image="/og/og-modules.jpg"
          schema={{
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Vidyapeeth360 School ERP Modules",
            url: `${seoOrigin}/modules`,
            description: "A directory of connected school ERP capabilities for Indian schools, grouped across nine school workspaces.",
          }}
        />
        <MarketingTopBar />
        <ScrollProgressBar />
        <main className="flex-1">
          {/* Scenic scroll stage (2026-08-22): the knowledge-canopy painting rides as a sticky
              full-viewport backdrop under the header + guided story + buyer guides — three
              sections — with their fills in campus-glass so the artwork follows the scroll.
              Light theme only; dark restores the solid fills and hides the stage. */}
          <div className="hero-campus-stage-wrap">
            <HeroCampusArt artwork="modules" stage veilClassName="hero-campus-veil-modules" />
            <section className="mx-auto max-w-6xl px-5 pb-24 pt-12 sm:pt-16 lg:pb-36">
              <div className="min-w-0 max-w-4xl">
              <p className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-1.5 text-[13px] font-semibold text-ink-2">
                <LayoutGrid className="h-4 w-4 text-primary" aria-hidden="true" /> Product catalogue
              </p>
              <h1 className="mt-5 max-w-3xl font-heading text-[36px] font-bold leading-[1.04] tracking-[-0.035em] text-ink sm:text-[48px] lg:text-[58px]">
                Find the school workflow you need to improve.
              </h1>
              <p className="mt-5 max-w-2xl text-[16px] leading-relaxed text-ink-2 sm:text-[17px]">
                Browse {MODULE_CATALOG.length} documented capabilities across {MODULE_WORKSPACES.length} connected workspaces. Each result identifies whether it is a module, feature, report, integration, mobile experience or governed Aira capability.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <a
                  href="#module-search"
                  data-testid="modules-hero-search"
                  onClick={() => track("module_discovery_selected", { placement: "modules_hero", destination: "search" })}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-[13.5px] font-semibold text-primary-fg transition-[background-color,transform] hover:bg-primary/90 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2"
                >
                  <Search className="h-4 w-4" aria-hidden="true" /> Search all capabilities
                </a>
                <a
                  href="#product-story"
                  data-testid="modules-hero-story"
                  onClick={() => track("module_discovery_selected", { placement: "modules_hero", destination: "product_story" })}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-line bg-surface px-5 py-2.5 text-[13.5px] font-semibold text-ink-2 transition-[border-color,color,transform] hover:border-primary/35 hover:text-primary active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                >
                  See the guided tour <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
              <ul className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-[13px] font-semibold text-ink-2" aria-label="What every capability page includes">
                <li className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-positive" aria-hidden="true" /> Every capability described as it ships</li>
                <li className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-positive" aria-hidden="true" /> Role and audience indicators</li>
                <li className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-positive" aria-hidden="true" /> Shows which plans include each capability</li>
              </ul>
              </div>
            </section>

          <React.Suspense
            fallback={(
              <section
                id="product-story"
                className="min-h-[54rem] scroll-mt-28 border-y border-line bg-muted/25"
                aria-label="Loading product story"
                aria-busy="true"
              />
            )}
          >
            <ProductStoryShowcase
              sectionId="product-story"
              skipTargetId="module-search"
              skipLabel="Skip the guided tour and browse the catalogue"
              catalogueTargetId="module-search"
              onBookDemo={(sceneId) => {
                track("demo_request", { placement: "modules_product_story", scene: sceneId });
                requestBookDemo({
                  placement: "modules_product_story",
                  contextLabel: `${String(sceneId || "module").replace(/-/g, " ")} product story`,
                  analyticsTracked: true,
                });
              }}
            />
          </React.Suspense>

          <section
            id="school-job-guides"
            className="scroll-mt-28 border-b border-line campus-glass-surface"
            aria-labelledby="school-job-guides-heading"
            data-testid="buyer-guide-navigator"
          >
            <div className="mx-auto grid max-w-6xl gap-9 px-5 py-12 lg:grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)] lg:gap-16 lg:py-16">
              <div className="lg:sticky lg:top-32 lg:self-start">
                <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-accent">Explore by school job</p>
                <h2 id="school-job-guides-heading" className="mt-3 max-w-md font-heading text-[28px] font-bold leading-[1.12] tracking-[-0.025em] text-ink sm:text-[34px]">
                  Start with the outcome your team owns.
                </h2>
                <p className="mt-4 max-w-lg text-[14.5px] leading-relaxed text-ink-2">
                  Six buyer guides connect the capabilities behind a complete school job. Three capability guides explain narrower shipped scope. None adds to the 115-capability catalogue or changes what your plan includes.
                </p>
                <a
                  href="#module-search"
                  className="mt-6 inline-flex min-h-11 items-center gap-2 rounded-full border border-line bg-canvas px-4 py-2 text-[13px] font-semibold text-ink-2 transition-colors hover:border-primary/35 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                >
                  Browse all {MODULE_CATALOG.length} capabilities <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>

              <nav aria-label="Buyer and capability guides">
                <ol className="border-y border-line">
                  {MODULE_BUYER_GUIDES.map((guide, index) => (
                    <li key={guide.key} className="border-b border-line last:border-b-0">
                      <Link
                        to={guide.path}
                        data-testid={`buyer-guide-nav-link-${guide.key}`}
                        className="group grid min-h-[88px] grid-cols-[34px_minmax(0,1fr)_24px] items-start gap-3 py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/55 sm:grid-cols-[44px_minmax(0,1fr)_24px] sm:py-5"
                        onClick={() => track("buyer_guide_opened", { guide: guide.key, placement: "modules_job_navigator" })}
                      >
                        <span className="pt-1 font-heading text-[12px] font-bold tabular-nums tracking-[0.12em] text-ink-subtle" aria-hidden="true">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <strong className="font-heading text-[16px] font-semibold tracking-tight text-ink transition-colors group-hover:text-primary sm:text-[17px]">
                              {buyerGuideTitle(guide)}
                            </strong>
                            <span className="text-[12px] font-bold uppercase tracking-[0.1em] text-ink-subtle">{buyerGuideKind(guide)}</span>
                          </span>
                          <span className="mt-1.5 block max-w-2xl text-[12.5px] leading-relaxed text-ink-subtle sm:text-[13px]">
                            {buyerGuideSummary(guide)}
                          </span>
                        </span>
                        <ArrowRight className="mt-1 h-4 w-4 text-ink-subtle transition-[color,transform] group-hover:translate-x-1 group-hover:text-primary" aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ol>
              </nav>
            </div>
          </section>

          <section className="border-y border-line campus-glass-surface" aria-labelledby="catalogue-language-heading">
            <div className="mx-auto max-w-6xl px-5 py-7 sm:py-8">
              <h2 id="catalogue-language-heading" className="sr-only">How the product catalogue is organised</h2>
              <dl className="grid gap-5 md:grid-cols-3 md:gap-0 md:divide-x md:divide-line">
                {[
                  [Layers3, "Workspace", "A broad operating area such as Academics, Finance or Campus Operations."],
                  [Workflow, "Module", "A working system that completes a school job, such as Admissions or Fee Collection."],
                  [ListChecks, "Capability", "A feature, report, integration or experience available inside a module or across the platform."],
                ].map(([Icon, term, description]) => (
                  <div key={term} className="md:px-6 md:first:pl-0 md:last:pr-0">
                    <dt className="flex items-center gap-3 font-heading text-[14px] font-semibold text-ink">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary" aria-hidden="true">
                        <Icon className="h-5 w-5" strokeWidth={1.8} />
                      </span>
                      {term}
                    </dt>
                    <dd className="mt-2 pl-[52px] text-[12.5px] leading-5 text-ink-subtle">{description}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>

          <section id="aira" className="scroll-mt-28 border-b border-line bg-ai/[0.045]" aria-labelledby="aira-layer-heading">
            <div className="mx-auto flex max-w-6xl flex-col gap-5 px-5 py-7 sm:flex-row sm:items-center sm:justify-between sm:py-8">
              <div className="flex max-w-3xl items-start gap-3.5">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-ai/10 text-ai" aria-hidden="true">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-[12px] font-bold uppercase tracking-[0.11em] text-ai-ink">Cross-workspace intelligence</p>
                  <h2 id="aira-layer-heading" className="mt-1 font-heading text-[19px] font-semibold tracking-tight text-ink sm:text-[21px]">
                    Aira works across school workflows — not as a separate department.
                  </h2>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2">
                    Its {AIRA_CAPABILITIES.length} documented capabilities appear inside the workspace they support, with permission scope, human review and availability explained on each page.
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    updateFilter({ group: "aira" });
                    track("module_category_selected", {
                      workspace: "aira",
                      filter_type: "cross_workspace_layer",
                    });
                    requestAnimationFrame(() => document.getElementById("module-catalogue")?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" }));
                  }}
                  className="inline-flex min-h-11 items-center rounded-full border border-ai/25 bg-surface px-4 py-2 text-[12.5px] font-semibold text-ai-ink transition-colors hover:border-ai/45 hover:bg-ai/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ai/45"
                >
                  Show Aira capabilities
                </button>
                <Link to="/aira" className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-ai px-4 py-2 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ai/50 focus-visible:ring-offset-2">
                  Explore Aira <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </section>

          {/* Search stays immediately beside its filters and result set; the story never splits them. */}
          <section id="module-search" tabIndex={-1} className="mx-auto max-w-6xl scroll-mt-28 px-5 py-10 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focusring sm:py-12" aria-labelledby="module-search-heading">
            <div className="min-w-0 rounded-3xl border border-line bg-surface p-5 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.48)] sm:p-6">
              <p id="module-search-heading" className="font-heading text-[18px] font-semibold text-ink">Find a school job or exact capability</p>
              <p className="mt-1 text-[13px] leading-relaxed text-ink-subtle">Search by job, capability, or team. Guide matches appear separately from the capability count.</p>
              <div className="mt-5 text-left">
                <label htmlFor="module-search-input" className="sr-only">Search buyer and capability guides, modules and capabilities</label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
                  <input id="module-search-input" type="search" name="module-search" value={query}
                    onChange={(event) => updateFilter({ q: event.target.value })}
                    onBlur={() => { if (query) track("module_search_used", { query_length: query.length, results: resultCount, guide_results: visibleBuyerGuides.length }); }}
                    placeholder="Search fees, payroll, timetable, transport…" autoComplete="off"
                    className="h-[52px] w-full rounded-2xl border border-line bg-surface py-3.5 pl-12 pr-12 text-[15px] text-ink shadow-sm outline-none transition-[border-color,box-shadow] placeholder:text-ink-subtle focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/10" />
                  {query && <button type="button" onClick={() => updateFilter({ q: "" })} aria-label="Clear module search"
                    className="absolute right-3 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-xl text-ink-subtle hover:bg-muted hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50">
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>}
                </div>
              </div>
              {visibleBuyerGuides.length > 0 && (
                <div className="mt-5 border-y border-line" data-testid="buyer-guide-search-results">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 py-3">
                    <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-ink-subtle">Matching guides</p>
                    <p className="text-[11.5px] text-ink-subtle">Guides organise school jobs or explain narrow shipped scope.</p>
                  </div>
                  <ul className="border-t border-line">
                    {visibleBuyerGuides.map((guide) => (
                      <li key={guide.key} className="border-b border-line last:border-b-0">
                        <Link
                          to={guide.path}
                          data-testid={`buyer-guide-search-link-${guide.key}`}
                          className="group grid min-h-14 grid-cols-[minmax(0,1fr)_20px] items-center gap-3 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/55"
                          onClick={() => track("buyer_guide_opened", { guide: guide.key, placement: "modules_search" })}
                        >
                          <span className="min-w-0">
                            <strong className="block text-[13.5px] font-semibold text-ink transition-colors group-hover:text-primary">{buyerGuideTitle(guide)}</strong>
                            <span className="mt-0.5 block text-[11.5px] leading-relaxed text-ink-subtle">{buyerGuideKind(guide)}</span>
                          </span>
                          <ArrowRight className="h-4 w-4 text-ink-subtle transition-[color,transform] group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden="true" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4 text-[12px] text-ink-subtle">
                <span><strong className="font-semibold text-ink">{resultCount}</strong> capabilities shown</span>
                <span className="flex flex-wrap items-center justify-end gap-x-2">
                  <a
                    href="#product-story"
                    data-testid="modules-catalogue-story"
                    onClick={() => track("product_story_opened", { placement: "modules_catalogue_intro" })}
                    className="inline-flex min-h-11 items-center rounded-lg px-2 font-semibold text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                  >
                    See the guided tour
                  </a>
                  <Link to="/pricing" className="inline-flex min-h-11 items-center gap-1 rounded-lg px-2 font-semibold text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50">Compare plans <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></Link>
                </span>
              </div>
            </div>
          </section>

          <nav
            id="module-catalogue"
            tabIndex={-1}
            aria-label="Operating workspaces and catalogue filters"
            className="sticky top-[112px] z-20 scroll-mt-[112px] border-y border-line bg-canvas/95 backdrop-blur-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focusring"
          >
            <div className="mx-auto flex max-w-6xl gap-2 overflow-x-auto px-5 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {[
                { k: "all", label: "All", modules: MODULE_CATALOG },
                ...MODULE_WORKSPACES,
                { k: "aira", label: "Aira layer", modules: AIRA_CAPABILITIES },
              ].map((group) => {
                const active = selectedGroup === group.k;
                return <button key={group.k} type="button" aria-pressed={active}
                  onClick={() => {
                    updateFilter({ group: group.k });
                    track("module_category_selected", {
                      workspace: group.k,
                      filter_type: group.k === "aira" ? "cross_workspace_layer" : "workspace",
                    });
                  }}
                  className={`min-h-11 shrink-0 rounded-full border px-3.5 py-2 text-[12.5px] font-semibold transition-[background-color,border-color,color] focus:outline-none focus-visible:ring-2 ${group.k === "aira" ? `focus-visible:ring-ai/50 ${active ? "border-ai bg-ai text-white" : "border-ai/25 bg-ai/5 text-ai-ink hover:border-ai/45"}` : `focus-visible:ring-primary/50 ${active ? "border-primary bg-primary text-primary-fg" : "border-line bg-surface text-ink-2 hover:border-primary/40 hover:text-ink"}`}`}>
                  {group.label} <span className={active ? "text-primary-fg" : "text-ink-subtle"}>{group.modules.length}</span>
                </button>;
              })}
            </div>
          </nav>

          <p className="sr-only" aria-live="polite">
            {resultCount} capabilities and {visibleBuyerGuides.length} matching guides found
          </p>

          {/* The workspaces — nine buyer-facing operating areas over the one derived catalogue. */}
          {visibleGroups.map((g) => {
            const GIcon = g.icon;
            return (
              /* PERF-7: the nine catalogue sections are the bulk of this route's 698 KB DOM, so
                 they skip rendering until they approach the viewport. The `print:` pair is not
                 decoration — content-visibility:auto blanks a section's contents in print, so
                 without the reset the printed/PDF'd module directory comes out empty below the
                 first screen. Applied per section (each already clips its own overflow and
                 reserves its intrinsic size) rather than through a global helper class, per the
                 DELTA-7 note in index.css. */
              <section key={g.k} id={g.k} aria-labelledby={`modules-${g.k}-h`} className="border-t border-line [content-visibility:auto] [contain-intrinsic-size:auto_900px] print:[content-visibility:visible] print:[contain-intrinsic-size:none]">
                <div className="mx-auto max-w-6xl px-5 py-10 sm:py-12">
                  <m.div {...reveal()} className="flex items-start gap-4">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl" style={{ background: `${g.color}14` }}>
                      <GIcon className="h-6 w-6" style={{ color: g.color }} aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <h2 id={`modules-${g.k}-h`} className="font-heading text-[22px] font-bold tracking-tight text-ink sm:text-[26px]">
                        {g.title}
                      </h2>
                      <p className="mt-1 text-[14px] font-semibold leading-relaxed text-ink-2 max-w-2xl">{g.headline}</p>
                      <p className="mt-1.5 max-w-3xl text-[13px] leading-relaxed text-ink-subtle">{g.desc}</p>
                      <p className="mt-2 text-[11.5px] font-semibold text-ink-subtle">Typical teams: {g.owners.join(" · ")}</p>
                    </div>
                    <span className="ml-auto hidden shrink-0 rounded-full border border-line bg-surface px-3 py-1 text-[12px] font-semibold text-ink-2 sm:inline-flex">
                      {g.modules.length} {g.modules.length === 1 ? "capability" : "capabilities"}
                    </span>
                  </m.div>
                  <m.div
                    className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3"
                    variants={reduce ? undefined : GRID_STAGGER}
                    initial={reduce ? false : "hidden"}
                    whileInView="show"
                    viewport={{ once: true, amount: 0.08 }}
                  >
                    {g.modules.map((mod) => (
                      <ModuleCard key={mod.key} mod={mod} reduce={reduce} />
                    ))}
                  </m.div>
                </div>
              </section>
            );
          })}

          {resultCount === 0 && (
            <section className="mx-auto max-w-2xl px-5 py-20 text-center" aria-labelledby="modules-empty-h">
              <h2 id="modules-empty-h" className="font-heading text-2xl font-bold text-ink">No matching individual capabilities</h2>
              <p className="mt-2 text-[14px] text-ink-2">
                {visibleBuyerGuides.length
                  ? "A matching guide appears above. You can open it or broaden the capability search."
                  : "Try a broader term or clear the active category."}
              </p>
              <button type="button" onClick={() => setSearchParams({}, { replace: true })}
                className="mt-5 rounded-full bg-primary px-5 py-2.5 text-[13px] font-semibold text-primary-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2">
                Show all capabilities
              </button>
            </section>
          )}

          {/* Close — one ask. */}
          <section id="modules-close" tabIndex={-1} className="mx-auto max-w-6xl scroll-mt-28 px-5 py-14 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focusring sm:py-16">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--vp-close-band-from)] via-[var(--vp-close-band-via)] to-[var(--vp-close-band-to)] p-8 text-center sm:p-12">
              <div className="pointer-events-none absolute -top-20 -right-16 h-72 w-72 rounded-full bg-orange/15 blur-3xl" aria-hidden="true" />
              <h2 className="mx-auto max-w-2xl font-heading text-[26px] font-bold leading-tight tracking-tight text-white sm:text-[32px]">
                Define the right operating scope before rollout.
              </h2>
              <p className="mx-auto mt-3 max-w-xl leading-relaxed text-white/90">
                See what each plan includes, what is still rolling out, and what to set up before go-live.
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 shadow-lg"
                  onClick={() => { track("trial_cta_click", { placement: "modules_hub" }); navigate("/register"); }}>
                  {CTA_PRIMARY_LABEL} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Button>
                <BookDemoLink
                  contextLabel="the complete Vidyapeeth360 module catalogue"
                  placement="modules_hub"
                  size="lg"
                  variant="ghost"
                  className="border border-white/40 text-white hover:bg-white/10"
                >
                  {CTA_SECONDARY_LABEL}
                </BookDemoLink>
              </div>
            </div>
          </section>
          </div>
        </main>
        <SiteFooter />
      </div>
  );
}

// The content component must mount below MarketingMotion so the manual Pause control participates
// in every custom animation gate, not only Framer Motion's global transform handling.
export default function ModulesHub() {
  return <MarketingMotion><ModulesHubContent /></MarketingMotion>;
}
